import { Header, Text } from '@rbilabs/universal-components';
import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { ActionsheetContent, ActionsheetWrapper } from 'components/action-sheet';
import { primitive } from 'styles/constants/primitives';
import { WEB_EXTRA_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';

import { AboutBadgesLogo } from './about-badges-logo';

export const AboutBadgesModal = ({ onClose }: { onClose: () => void }) => {
  const { formatMessage } = useIntl();

  const descriptionLines = [
    formatMessage({
      id: 'whenYouHitCertainMilestones',
      defaultMessage:
        'When you hit certain milestones in the BK® App, like ordering your favorites or trying something new, you earn a badge.',
    }),
    formatMessage({
      id: 'moreYouDoTheMoreYouUnlock',
      defaultMessage:
        'The more you do, the more you unlock. And some badges? Well, let’s just say they’re harder to get — and way more fun to flex.',
    }),
  ];

  return (
    <ActionsheetWrapper isOpen onOpenEventMessage="Open about badges modal" onClose={onClose}>
      <ActionsheetContent style={styles.wrapper}>
        <View style={styles.header}>
          <Header accessibilityLevel={2} style={styles.mainHeaderText}>
            {formatMessage({ id: 'bkBadges', defaultMessage: 'BK Badges' })}
          </Header>
          {!isMobile() && (
            <Pressable
              onPress={onClose}
              accessible
              accessibilityRole="button"
              accessibilityLabel={formatMessage({ id: 'close' })}
              style={styles.closeButton}
            >
              <Icon variant="cancel" title={formatMessage({ id: 'cancel' })} />
            </Pressable>
          )}
        </View>
        <View style={styles.container}>
          <Text style={styles.subtitle}>
            {formatMessage({
              id: 'yourWayToShowOff',
              defaultMessage: 'Your way to show off just how much you rule!',
            })}
          </Text>
          <AboutBadgesLogo style={styles.image} />
          <View style={[styles.descriptionSection, !isMobile() && styles.descriptionSectionWeb]}>
            <Header accessibilityLevel={3} style={styles.subHeaderText}>
              {formatMessage({ id: 'howItWorksLower', defaultMessage: 'How it works' })}:
            </Header>
            {descriptionLines.map((line, idx) => (
              <View key={idx} style={styles.descriptionLine}>
                <View style={styles.descriptionLineNumberContainer}>
                  <Text style={styles.descriptionLineNumber}>{idx + 1}</Text>
                </View>
                <Text style={styles.descriptionLineText}>{line}</Text>
              </View>
            ))}
          </View>
          <ActionButton
            variant={ActionButtonVariants.OUTLINE}
            onPress={onClose}
            alignSelf="stretch"
          >
            {formatMessage({ id: 'letsGo', defaultMessage: `Let's go!` })}
          </ActionButton>
        </View>
      </ActionsheetContent>
    </ActionsheetWrapper>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: primitive.$white,
    alignItems: 'center',
  },
  container: {
    alignItems: 'center',
    paddingBottom: primitive.spacing.$6,
    width: '100%',
    maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
  },
  header: {
    flexDirection: 'row',
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    marginTop: primitive.spacing.$4,
  },
  mainHeaderText: {
    marginBottom: primitive.spacing.$1,
    fontSize: primitive.fontSizes['2xl'],
    lineHeight: primitive.fontSizes['3xl'],
  },
  subHeaderText: {
    margin: 0,
    fontSize: primitive.fontSizes.md,
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  subtitle: {
    fontSize: primitive.fontSizes.xs,
    textAlign: 'center',
    marginBottom: primitive.spacing.$3,
  },
  image: {
    width: 113,
    height: 75,
    marginBottom: primitive.spacing.$5,
  },
  descriptionSection: {
    gap: primitive.spacing.$4,
    paddingHorizontal: primitive.spacing.$6,
    width: '100%',
    marginBottom: primitive.spacing.$8,
  },
  descriptionSectionWeb: {
    alignSelf: 'center',
  },
  descriptionLine: {
    flexDirection: 'row',
    gap: primitive.spacing.$2,
    alignItems: 'flex-start',
  },
  descriptionLineNumberContainer: {
    width: primitive.spacing.$6,
    height: primitive.spacing.$6,
    backgroundColor: primitive.bk.$bbqBrown,
    borderRadius: primitive.spacing.$3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionLineNumber: {
    fontSize: primitive.fontSizes.xs,
    color: primitive.$white,
  },
  descriptionLineText: {
    flex: 1,
    fontSize: primitive.fontSizes.xs,
  },
});
